import jsPDF from "jspdf";
import "jspdf-autotable";
import * as style from "./ReportStyle";

/**
 * OrderPage.js — RA Masale format (Purchase/Sales Order print)
 * ------------------------------------------------------------------
 * Entry point, same role as InvoiceA4PDFReport/Page.js. Builds the
 * jsPDF doc, calls the header/body/footer drawing functions in
 * order, then opens the PDF.
 *
 * IMPORTANT — Order API response shape:
 * Your order API returns `Data` as an ARRAY of orders, unlike the
 * invoice API which returns `Data` as a single OBJECT. So call this
 * with a single order object:
 *
 *     OrderReport_RAMasale(response.Data[0])
 *
 * not with the raw response or the whole array. If you ever need to
 * print several orders from one response, loop and call this once
 * per order (each opens its own PDF tab).
 *
 * `companyDetails` is optional here — unlike the invoice version,
 * the order payload already carries the seller's own details
 * (SupplierName, SupplierGSTIN, SupplierAddress, SupplierFssai), so
 * OrderReportStyle.js uses those directly and only falls back to
 * companyDetails / login details if they're missing.
 * ------------------------------------------------------------------
 */

/**
 * The order API's `Data` comes back as an ARRAY (see the sample
 * response), and depending on how the redux dispatcher wires this
 * up, it may hand this function the array, the raw API response, or
 * an already-unwrapped single order object. Normalize all three so
 * the report binds correctly regardless of which one arrives.
 */
const normalizeOrderData = (input) => {
    if (Array.isArray(input)) {
        return input[0] || {};
    }
    if (input && Array.isArray(input.Data)) {
        return input.Data[0] || {};
    }
    if (input && input.Data && typeof input.Data === "object") {
        return input.Data;
    }
    return input || {};
};

const pageHeder = (doc, data, companyDetails) => {
    style.pageBorder(doc);
    style.pageHeder(doc, data, companyDetails);
    const y = style.reportPartyAndOrderInfo(doc, data);
    return y;
};

const FOOTER_MIN_Y = 745;
const reportBody = (doc, data, headerBottomY) => {
    const tableEndY = style.tableBody(doc, data, headerBottomY);
    const footerStartY = Math.max(tableEndY, FOOTER_MIN_Y);
    style.reportFooterTotals(doc, data, footerStartY);
};

const pageFooter = (doc) => {
    style.pageFooter(doc);
};

const OrderReport_RAMasale = (dataInput, companyDetails) => {
    const data = normalizeOrderData(dataInput);
    const doc = new jsPDF("p", "pt", "a4");

    const headerBottomY = pageHeder(doc, data, companyDetails);
    reportBody(doc, data, headerBottomY);
    pageFooter(doc);

    doc.setProperties({
        title: `OrderReport/${data.OrderDate}-${data.CustomerName}`,
    });

    const pdfUrl = URL.createObjectURL(doc.output("blob"));
    window.open(pdfUrl);
};

export default OrderReport_RAMasale;